/**
 * /policies/shifts/patterns — Session 3c PR 9.
 * Owning agent: Tashghil.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { ShiftPatternItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function ShiftPatternsPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-shift-patterns", le],
    queryFn: () => session3cApi.listShiftPatterns(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<ShiftPatternItem>[] = [
    {
      headerKey: "policies.shift_patterns.col.name",
      label: "Pattern name",
      render: (r) => <span className="font-medium">{r.pattern_name}</span>,
    },
    {
      headerKey: "policies.shift_patterns.col.cycle",
      label: "Cycle",
      render: (r) => <span className="text-xs text-text-secondary">{r.cycle_description}</span>,
    },
    {
      headerKey: "policies.shift_patterns.col.assignments",
      label: "Default assignments",
      render: (r) => <span className="font-mono text-xs">{r.default_assignments_count}</span>,
    },
    {
      headerKey: "policies.shift_patterns.col.employees",
      label: "On pattern",
      render: (r) => <span className="font-mono text-xs">{r.employees_on_pattern}</span>,
    },
  ];

  return (
    <PolicyListPage<ShiftPatternItem>
      title={t("policies.shift_patterns.title", "Shift Patterns")}
      subtitle={t("policies.shift_patterns.subtitle", "Rotating shifts · 4-on-4-off · split shifts · seasonal patterns.")}
      agent="tashghil"
      domain="shift_patterns"
      newButtonLabel={t("policies.shift_patterns.cta.new", "New Pattern")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.shift_patterns.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.shift_patterns.suggestion.clean_body", "Patterns define cycle structures. Per-employee assignment lives on /operations/attendance/shift-roster."),
          primaryActionLabel: t("policies.shift_patterns.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "shift-patterns-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.shift_patterns.title", "Shift Patterns"),
        window_label: t("policies.shift_patterns.window", "across the workforce"),
      }}
    />
  );
}
